import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ── AUTENTICAZIONE ────────────────────────────────────────────────────────────
// L'accesso avviene con un codice a sei cifre inviato per email. Dopo la prima
// verifica la sessione resta valida su quel dispositivo e si rinnova da sola:
// il codice non viene piu' richiesto.

export async function sendLoginCode(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.toLowerCase().trim(),
    // Serve true: i musicisti registrati prima dell'autenticazione hanno un
    // profilo ma nessun account, e al primo accesso l'account va creato.
    options: { shouldCreateUser: true },
  });
  if (error) throw error;
}

export async function verifyLoginCode(email, code) {
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.toLowerCase().trim(),
    token: code.trim(),
    type: 'email',
  });
  if (error) throw error;
  return data.session;
}

// Aggancia l'account appena verificato a un profilo musicista preesistente
// con la stessa email. Non fa niente se l'aggancio c'e' gia'.
export async function linkAuthUser() {
  const { error } = await supabase.rpc('link_auth_user');
  if (error) console.error('link_auth_user:', error);
}

// Il profilo di chi ha effettuato l'accesso, o null se deve ancora registrarsi.
// Cerca prima tra i musicisti, poi tra le location: anche queste accedono
// con la propria email.
export async function getMyProfile() {
  const { data: mus, error: em } = await supabase.rpc('my_musician');
  if (em) throw em;
  const musician = Array.isArray(mus) ? mus[0] : mus;
  if (musician) return { ...musician, accountType: musician.account_type || 'musician' };

  const { data: ven, error: ev } = await supabase.rpc('my_venue');
  if (ev) throw ev;
  const venue = Array.isArray(ven) ? ven[0] : ven;
  if (venue) return { ...venue, accountType: 'venue' };

  return null;
}

export async function getCurrentSession() {
  const { data } = await supabase.auth.getSession();
  return data?.session || null;
}

// getCurrentSession() legge soltanto quello che e' salvato nel browser: un
// lasciapassare resta li' valido fino alla scadenza anche se nel frattempo
// l'account e' stato cancellato. Questa invece lo chiede al server.
export async function sessionIsValid() {
  const { data, error } = await supabase.auth.getUser();
  return !error && !!data?.user;
}

export async function signOut() {
  await supabase.auth.signOut();
}

// ── MUSICIANS ─────────────────────────────────────────────────────────────────
export async function getMusicians() {
  const { data, error } = await supabase.from('musicians').select('*').order('name');
  if (error) throw error; return data;
}
// Risponde solo si' o no: chi non ha ancora un account non deve poter
// leggere la riga di un altro, ne' scoprirne i contatti.
export async function emailExists(email) {
  const { data, error } = await supabase.rpc('email_exists', { p_email: email });
  if (error) throw error;
  return !!data;
}
export async function createMusician(musician) {
  const { data, error } = await supabase.from('musicians').insert([{ ...musician, email: musician.email.toLowerCase().trim() }]).select().single();
  if (error) throw error; return data;
}
export async function updateMusician(id, data) {
  const { data: updated, error } = await supabase.from('musicians').update({ ...data, email: data.email?.toLowerCase().trim() }).eq('id', id).select().single();
  if (error) throw error; return updated;
}
// La locandina finisce nell'anteprima dei link su WhatsApp, che scarta le
// immagini troppo pesanti: una foto dal telefono pesa facilmente 3 MB.
// Prima di caricarla la si riduce a 1200 px sul lato lungo, in JPEG, che di
// solito sta sotto i 300 KB. Se qualcosa va storto si carica l'originale.
async function comprimiLocandina(file, latoMax = 1200, qualita = 0.82) {
  try {
    if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
    const bmp = await createImageBitmap(file);
    const scala = Math.min(1, latoMax / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scala);
    canvas.height = Math.round(bmp.height * scala);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', qualita));
    return blob && blob.size < file.size ? blob : file;
  } catch (e) {
    console.error('compressione locandina:', e);
    return file;
  }
}

export async function uploadEventImage(file) {
  const pronta = await comprimiLocandina(file);
  const ext = pronta === file ? file.name.split('.').pop() : 'jpg';
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('event-images').upload(path, pronta,
    { upsert: false, contentType: pronta === file ? file.type : 'image/jpeg' });
  if (error) throw error;
  const { data } = supabase.storage.from('event-images').getPublicUrl(path);
  return data.publicUrl;
}
export async function uploadVenueImage(file) {
  const ext = file.name.split('.').pop();
  const path = `venue-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage.from('event-images').upload(path, file, { upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from('event-images').getPublicUrl(path);
  return data.publicUrl;
}
export async function uploadAvatarImage(musicianId, blob) {
  const path = `${musicianId}-${Date.now()}.jpg`;
  const { error } = await supabase.storage.from('avatars').upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return data.publicUrl;
}

// ── APP SETTINGS ──────────────────────────────────────────────────────────────
export async function getAppSetting(key) {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', key).single();
  if (error && error.code !== 'PGRST116') throw error;
  return data?.value || null;
}
export async function setAppSetting(key, value) {
  const { error } = await supabase.from('app_settings').upsert({ key, value }, { onConflict: 'key' });
  if (error) throw error;
}

// Elimina definitivamente anche l'account verificato, non solo il profilo.
// Il browser da solo non puo' farlo: passa dalla funzione delete_auth_user,
// che verifica che a chiedere sia il diretto interessato o un amministratore.
async function deleteAuthAccount(authUserId) {
  if (!authUserId) return;
  const { error } = await supabase.rpc('delete_auth_user', { target_auth_id: authUserId });
  if (error) console.error('delete_auth_user:', error);
}

// Svuota il profilo e cancella l'account verificato, in un colpo solo e con
// il controllo dei permessi lato database (vedi supabase/cancellazione.sql).
// I contenuti — brani, idee, avvisi — restano, attribuiti a "Utente cancellato".
export async function deleteMusician(id) {
  const { error } = await supabase.rpc('delete_musician_account', { target: id });
  if (error) throw error;
}
export async function countAdmins() {
  const { count, error } = await supabase.from('musicians').select('*', { count: 'exact', head: true }).eq('is_admin', true);
  if (error) throw error; return count;
}
export async function setAdminStatus(musicianId, isAdmin) {
  const { data, error } = await supabase.rpc('set_admin_status',
    { p_musician_id: musicianId, p_is_admin: isAdmin });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}
export async function setOrganizerPermissions(musicianId, canPrivate, canPublic) {
  const { data, error } = await supabase.rpc('set_organizer_permissions',
    { p_musician_id: musicianId, p_private: canPrivate, p_public: canPublic });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}
export async function requestOrganizerStatus() {
  const { error } = await supabase.rpc('request_organizer_status');
  if (error) throw error;
}

// ── VENUES ────────────────────────────────────────────────────────────────────
export async function getVenues() {
  const { data, error } = await supabase.from('venues').select('*').order('name');
  if (error) throw error; return data;
}
export async function createVenue(venue) {
  const { data, error } = await supabase.from('venues').insert([{ ...venue, email: venue.email.toLowerCase().trim() }]).select().single();
  if (error) throw error; return data;
}
export async function updateVenue(id, data) {
  const { data: updated, error } = await supabase.from('venues').update({ ...data, email: data.email?.toLowerCase().trim() }).eq('id', id).select().single();
  if (error) throw error; return updated;
}
export async function deleteVenue(id) {
  const { data: profilo } = await supabase
    .from('venues').select('auth_user_id').eq('id', id).single();
  const { error } = await supabase.from('venues').delete().eq('id', id);
  if (error) throw error;
  await deleteAuthAccount(profilo?.auth_user_id);
}

// ── VENUE EQUIPMENT ───────────────────────────────────────────────────────────
export async function getVenueEquipment(venueId) {
  const { data, error } = await supabase.from('venue_equipment').select('*').eq('venue_id', venueId);
  if (error) throw error; return data;
}
export async function setVenueEquipment(venueId, items) {
  await supabase.from('venue_equipment').delete().eq('venue_id', venueId);
  if (!items.length) return;
  const { error } = await supabase.from('venue_equipment').insert(items.map(i => ({ venue_id: venueId, item: i.item, category: 'standard' })));
  if (error) throw error;
}

// ── EVENTS ────────────────────────────────────────────────────────────────────
export async function getEvents() {
  const { data, error } = await supabase
    .from('events')
    .select('*, event_participants(musician_id), event_organizers(musician_id), tracks!event_id(id, type)')
    .order('date', { ascending: true });
  if (!error) return data;
  // Rete di sicurezza: se supabase/co-organizzatori.sql non e' ancora stato
  // lanciato la tabella non esiste, e senza questo l'app non caricherebbe
  // nessuna serata.
  const { data: senza, error: e2 } = await supabase
    .from('events')
    .select('*, event_participants(musician_id), tracks!event_id(id, type)')
    .order('date', { ascending: true });
  if (e2) throw e2; return senza;
}

// ── CO-ORGANIZZATORI ──────────────────────────────────────────────────────────
// Nominare e togliere passa da funzioni del database che controllano chi
// chiede: solo l'organizzatore principale o l'admin nominano, e un
// co-organizzatore puo' togliersi da solo.
export async function getEventOrganizers(eventId) {
  const { data, error } = await supabase
    .from('event_organizers').select('musician_id').eq('event_id', eventId);
  if (error) return [];
  return (data || []).map(r => r.musician_id);
}
export async function addEventOrganizer(eventId, musicianId) {
  const { error } = await supabase.rpc('add_event_organizer',
    { p_event_id: eventId, p_musician_id: musicianId });
  if (error) throw error;
}
export async function removeEventOrganizer(eventId, musicianId) {
  const { error } = await supabase.rpc('remove_event_organizer',
    { p_event_id: eventId, p_musician_id: musicianId });
  if (error) throw error;
}
export async function createEvent(event) {
  // Una serata nuova nasce con le proposte APERTE. players_managed vuol dire
  // "proposte chiuse" (nome al contrario), e nel database il valore di
  // partenza era true: ogni serata nuova nasceva chiusa e i musicisti non
  // vedevano "Proponi un brano". Lo scriviamo esplicito, cosi' non dipende
  // da come e' configurato il database.
  const { data, error } = await supabase.from('events').insert([{ players_managed: false, ...event }]).select().single();
  if (error) throw error; return data;
}
export async function updateEvent(id, data) {
  const { data: updated, error } = await supabase.from('events').update(data).eq('id', id).select().single();
  if (error) throw error; return updated;
}
export async function deleteEvent(id) {
  const { error } = await supabase.from('events').delete().eq('id', id);
  if (error) throw error;
}
export async function updateEventLock(id, ordering_locked) {
  const { error } = await supabase.from('events').update({ ordering_locked }).eq('id', id);
  if (error) throw error;
}

// Mostra o nasconde ai musicisti una card della serata ("ideas", "live" o
// "equipment"). Colonne create da supabase/card-serata.sql.
export async function setEventCardVisible(id, card, visible) {
  const colonna = card === 'ideas' ? 'show_ideas' : card === 'live' ? 'show_live' : 'show_equipment';
  const { error } = await supabase.from('events').update({ [colonna]: !!visible }).eq('id', id);
  if (error) throw error;
}

export async function updateEventInvitesLock(id, invites_locked) {
  const { error } = await supabase.from('events').update({ invites_locked }).eq('id', id);
  if (error) throw error;
}

// ── EVENT EQUIPMENT (venue) ───────────────────────────────────────────────────
export async function getEventEquipment(eventId) {
  const { data, error } = await supabase.from('event_equipment').select('*').eq('event_id', eventId);
  if (error) throw error; return data;
}
export async function setEventEquipment(eventId, items) {
  await supabase.from('event_equipment').delete().eq('event_id', eventId).eq('provided_by_venue', true);
  if (!items.length) return;
  const { error } = await supabase.from('event_equipment').insert(items.map(i => ({ event_id: eventId, item: i, category: 'standard', provided_by_venue: true })));
  if (error) throw error;
}
export async function addEventEquipmentItem(eventId, item) {
  const { error } = await supabase.from('event_equipment').insert([{ event_id: eventId, item, category: 'standard', provided_by_venue: false }]);
  if (error) throw error;
}
export async function removeEventEquipmentItem(id) {
  const { error } = await supabase.from('event_equipment').delete().eq('id', id);
  if (error) throw error;
}

// ── EVENT MUSICIAN EQUIPMENT ──────────────────────────────────────────────────
export async function getEventMusicianEquipment(eventId) {
  const { data, error } = await supabase.from('event_musician_equipment').select('*').eq('event_id', eventId);
  if (error) throw error; return data;
}
export async function addMusicianEquipment(eventId, musicianId, item) {
  const { error } = await supabase.from('event_musician_equipment').insert([{ event_id: eventId, musician_id: musicianId, item }]);
  if (error) throw error;
}
export async function removeMusicianEquipment(id) {
  const { error } = await supabase.from('event_musician_equipment').delete().eq('id', id);
  if (error) throw error;
}

// ── EVENT REQUESTS ────────────────────────────────────────────────────────────
export async function getEventRequests(eventId) {
  const { data, error } = await supabase.from('event_requests').select('*').eq('event_id', eventId);
  if (error) throw error; return data;
}
export async function requestJoinEvent(eventId, musicianId) {
  const { error } = await supabase.from('event_requests').upsert([{ event_id: eventId, musician_id: musicianId, status: 'pending' }], { onConflict: 'event_id,musician_id' });
  if (error) throw error;
}
export async function approveEventRequest(eventId, musicianId) {
  const { error } = await supabase.from('event_requests').update({ status: 'approved' }).eq('event_id', eventId).eq('musician_id', musicianId);
  if (error) throw error;
}
export async function rejectEventRequest(eventId, musicianId) {
  const { error } = await supabase.from('event_requests').update({ status: 'rejected' }).eq('event_id', eventId).eq('musician_id', musicianId);
  if (error) throw error;
}
export async function getMyEventRequest(eventId, musicianId) {
  const { data, error } = await supabase.from('event_requests').select('*').eq('event_id', eventId).eq('musician_id', musicianId).single();
  if (error && error.code !== 'PGRST116') throw error; return data || null;
}
export async function inviteToEvent(eventId, musicianId) {
  await supabase.from('event_requests').upsert(
    [{ event_id: eventId, musician_id: musicianId, status: 'invited' }],
    { onConflict: 'event_id,musician_id' }
  );
  await joinEventAsParticipant(eventId, musicianId, []);
}

// ── TRACKS ────────────────────────────────────────────────────────────────────
export async function getTracksForEvent(eventId) {
  const { data: tracks, error: te } = await supabase.from('tracks').select('*').eq('event_id', eventId).order('position', { ascending: true }).order('created_at', { ascending: true });
  if (te) throw te;
  if (!tracks.length) return [];

  // Normalizza posizioni se tutte uguali o tutte zero (brani mai riordinati)
  const positions = tracks.map(t => t.position ?? 0);
  const allSame = positions.every(p => p === positions[0]);
  if (allSame && tracks.length > 1) {
    await Promise.all(tracks.map((t, i) => supabase.from('tracks').update({ position: i }).eq('id', t.id)));
    tracks.forEach((t, i) => { t.position = i; });
  }

  const ids = tracks.map(t => t.id);
  const { data: players, error: pe } = await supabase.from('track_players').select('track_id, musician_id, role, tonality').in('track_id', ids);
  if (pe) throw pe;
  return tracks.map(t => ({ ...t, track_players: players.filter(p => p.track_id === t.id) }));
}
export async function addTrack(track) {
  const { data, error } = await supabase.from('tracks').insert([track]).select().single();
  if (error) throw error; return data;
}
export async function removeTrack(id) {
  const { error } = await supabase.from('tracks').delete().eq('id', id);
  if (error) throw error;
}
export async function updateTrack(id, data) {
  const { error } = await supabase.from('tracks').update(data).eq('id', id);
  if (error) throw error;
}
export async function updateTrackStatus(id, status) {
  const { error } = await supabase.from('tracks').update({ status }).eq('id', id);
  if (error) throw error;
}
export async function updateTrackPosition(id, position) {
  const { error } = await supabase.from('tracks').update({ position }).eq('id', id);
  if (error) throw error;
}
export async function addTrackToScaletta(trackId, eventId) {
  const { data: last } = await supabase
    .from('tracks').select('position')
    .eq('event_id', eventId).eq('in_scaletta', true)
    .order('position', { ascending: false }).limit(1);
  const nextPos = last?.length ? (last[0].position ?? 0) + 1 : 0;
  const { error } = await supabase.from('tracks').update({ in_scaletta: true, position: nextPos }).eq('id', trackId);
  if (error) throw error;
}
export async function removeTrackFromScaletta(trackId) {
  const { error } = await supabase.from('tracks').update({ in_scaletta: false }).eq('id', trackId);
  if (error) throw error;
}
// players_managed=true significa proposte CHIUSE — semantica invertita rispetto all'UI
export async function updateEventProposalsOpen(id, proposalsOpen) {
  const { error } = await supabase.from('events').update({ players_managed: !proposalsOpen }).eq('id', id);
  if (error) throw error;
}
export async function updateEventNextTrack(id, trackId) {
  const { error } = await supabase.from('events').update({ next_track_id: trackId || null }).eq('id', id);
  if (error) throw error;
}

// ── TRACK PLAYERS ─────────────────────────────────────────────────────────────
export async function joinTrack(trackId, musicianId, role, tonalityOrNote = null) {
  // Per jam libera: role="" e tonalityOrNote è la free_note
  const isFree = role === "";
  const payload = isFree
    ? { track_id: trackId, musician_id: musicianId, role: "", tonality: null, free_note: tonalityOrNote }
    : { track_id: trackId, musician_id: musicianId, role, tonality: tonalityOrNote };
  const { error } = await supabase.from('track_players').upsert([payload], { onConflict: 'track_id,musician_id' });
  if (error) throw error;
}
export async function leaveTrack(trackId, musicianId) {
  const { error } = await supabase.from('track_players').delete().eq('track_id', trackId).eq('musician_id', musicianId);
  if (error) throw error;
}

// ── EVENT MESSAGES ────────────────────────────────────────────────────────────
export async function updateEventWhatsappLink(id, link) {
  const { error } = await supabase.from('events').update({ whatsapp_group_link: link || null }).eq('id', id);
  if (error) throw error;
}
export async function getEventMessages(eventId) {
  const { data, error } = await supabase.from('event_messages').select('*').eq('event_id', eventId).eq('post_type', 'avviso').order('created_at', { ascending: true });
  if (error) throw error; return data;
}
export async function sendEventMessage(eventId, musicianId, message, postType = 'avviso') {
  const { data, error } = await supabase.from('event_messages').insert([{ event_id: eventId, musician_id: musicianId, message, post_type: postType }]).select().single();
  if (error) throw error; return data;
}
export async function deleteEventMessage(id) {
  const { error } = await supabase.from('event_messages').delete().eq('id', id);
  if (error) throw error;
}

// ── EVENT PARTICIPANTS ────────────────────────────────────────────────────────
export async function getEventParticipants(eventId) {
  const { data, error } = await supabase
    .from('event_participants').select('*').eq('event_id', eventId);
  if (error) throw error; return data;
}

export async function joinEventAsParticipant(eventId, musicianId, instruments) {
  const { error } = await supabase.from('event_participants')
    .upsert([{ event_id: eventId, musician_id: musicianId, instruments }],
      { onConflict: 'event_id,musician_id' });
  if (error) throw error;
}

export async function leaveEventAsParticipant(eventId, musicianId) {
  // Prima prendo tutti i track_id dell'evento
  const { data: tracks } = await supabase.from('tracks')
    .select('id').eq('event_id', eventId);
  if (tracks && tracks.length > 0) {
    const trackIds = tracks.map(t => t.id);
    await supabase.from('track_players')
      .delete().in('track_id', trackIds).eq('musician_id', musicianId);
  }
  const { error } = await supabase.from('event_participants')
    .delete().eq('event_id', eventId).eq('musician_id', musicianId);
  if (error) throw error;
}


// ── ANTEPRIMA PUBBLICA DELL'EVENTO ───────────────────────────────────────────
// Usata da chi arriva dal link di invito senza account. Chiede una serata
// sola invece dell'intero database, e solo i campi che servono.
export async function getEventPreview(eventId) {
  // invite_image_url c'e' solo dopo supabase/immagine-invito.sql: se manca,
  // si richiede la serata senza, invece di dire "evento non trovato".
  const CAMPI = 'id, title, date, time_start, time_end, city, location, location_name, description, image_url, visibility, venue_id';
  let { data: ev, error } = await supabase
    .from('events').select(`${CAMPI}, invite_image_url`).eq('id', eventId).single();
  if (error && error.code !== 'PGRST116') {
    ({ data: ev, error } = await supabase.from('events').select(CAMPI).eq('id', eventId).single());
  }
  if (error) return null;

  if (ev.venue_id) {
    const { data: v } = await supabase
      .from('venues').select('name, city').eq('id', ev.venue_id).single();
    ev.venue = v || null;
  }

  const { data: parts } = await supabase
    .from('event_participants').select('musician_id').eq('event_id', eventId);
  const ids = (parts || []).map(p => p.musician_id);
  ev.participantCount = ids.length;
  ev.participantNames = [];
  if (ids.length) {
    // Solo i nomi, mai i contatti.
    const { data: nomi } = await supabase
      .from('musicians').select('name').in('id', ids.slice(0, 5));
    ev.participantNames = (nomi || []).map(m => m.name);
  }
  return ev;
}

// ── LIVE PAGE ─────────────────────────────────────────────────────────────────
// La pagina live e' pubblica: chiunque abbia il QR la apre senza account.
// Percio' non si chiede "tutto" della serata, ma solo i campi che servono a
// mostrarla. Fuori resta whatsapp_group_link, che e' di fatto una chiave
// d'ingresso al gruppo e non deve girare fuori dai partecipanti.
const LIVE_EVENT_COLS = [
  'id', 'title', 'date', 'time_start', 'time_end', 'city', 'location',
  'location_name', 'description', 'image_url', 'visibility', 'venue_id',
  'manager_id', 'ordering_locked', 'ordering_mode', 'invites_locked',
  'next_track_id', 'players_managed', 'setlist_mode'
].join(', ');

export async function getEventForLive(eventId) {
  let { data, error } = await supabase
    .from('events')
    .select(LIVE_EVENT_COLS)
    .eq('id', eventId)
    .single();
  // Rete di sicurezza: se una di quelle colonne non esiste piu', la pagina
  // live non deve restare bianca.
  if (error) {
    ({ data, error } = await supabase
      .from('events').select('*').eq('id', eventId).single());
  }
  if (error) throw error;
  if (data?.venue_id) {
    const { data: venue } = await supabase
      .from('venues')
      .select('background_image_url')
      .eq('id', data.venue_id)
      .single();
    data.venueBg = venue?.background_image_url || null;
  }
  return data;
}

export async function getTracksForLive(eventId) {
  const { data: tracks, error } = await supabase
    .from('tracks')
    .select('*, track_players(musician_id, role, musicians(name))')
    .eq('event_id', eventId)
    .neq('type', 'free')
    .order('in_scaletta', { ascending: false })
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return tracks || [];
}

// I like vengono conteggiati dal database in modo atomico: se venti persone
// votano nello stesso istante, ogni voto viene sommato. Il metodo "leggi il
// valore, aggiungi uno, riscrivilo" resta solo come rete di sicurezza se le
// funzioni SQL non sono ancora state create (vedi supabase/functions.sql).
export async function likeTrack(trackId) {
  const { error } = await supabase.rpc('increment_likes', { track_id: trackId });
  if (!error) return;
  const { data } = await supabase.from('tracks').select('likes').eq('id', trackId).single();
  await supabase.from('tracks').update({ likes: (data?.likes || 0) + 1 }).eq('id', trackId);
}

// Gli applausi si danno solo al brano che sta suonando, e solo mentre suona.
// Il controllo vero e' nel database: add_applause aggiorna solo una riga con
// status 'playing'. Chi non ha un account non puo' scrivere su tracks, quindi
// l'applauso passa per forza da qui.
export async function addApplause(trackId, count) {
  const n = Math.max(1, Math.min(20, Math.round(count || 1)));
  const { data, error } = await supabase.rpc('add_applause', {
    p_track_id: trackId, p_count: n });
  if (error) throw error;
  return data;   // null se il brano non sta piu' suonando
}

export async function resetApplause(eventId) {
  const { error } = await supabase.rpc('reset_applause', { p_event_id: eventId });
  if (error) throw error;
}

export async function unlikeTrack(trackId) {
  const { error } = await supabase.rpc('decrement_likes', { track_id: trackId });
  if (!error) return;
  const { data } = await supabase.from('tracks').select('likes').eq('id', trackId).single();
  await supabase.from('tracks').update({ likes: Math.max(0, (data?.likes || 0) - 1) }).eq('id', trackId);
}

export async function markTrackPlayed(trackId, played) {
  const { error } = await supabase.from('tracks').update({ played }).eq('id', trackId);
  if (error) throw error;
}

export async function resetEventLikes(eventId) {
  const { error } = await supabase.from('tracks').update({ likes: 0 }).eq('event_id', eventId);
  if (error) throw error;
}

// ── EVENT IDEAS ───────────────────────────────────────────────────────────────
export async function getEventIdeas(eventId) {
  const { data, error } = await supabase
    .from('event_ideas')
    .select('*, idea_likes(musician_id)')
    .eq('event_id', eventId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}
export async function addEventIdea(eventId, musicianId, text) {
  const { error } = await supabase
    .from('event_ideas')
    .insert([{ event_id: eventId, musician_id: musicianId, text }]);
  if (error) throw error;
}
export async function deleteEventIdea(id) {
  const { error } = await supabase.from('event_ideas').delete().eq('id', id);
  if (error) throw error;
}
export async function toggleIdeaLike(ideaId, musicianId, hasLiked) {
  if (hasLiked) {
    await supabase.from('idea_likes').delete().eq('idea_id', ideaId).eq('musician_id', musicianId);
  } else {
    await supabase.from('idea_likes').insert([{ idea_id: ideaId, musician_id: musicianId }]);
  }
}

